// src/ui/features/calculator/hooks/useRuleset.js
import { useEffect, useMemo, useState } from "react";
import { getRuleset } from "../../../firebase/functions";

const DEFAULT_TARGET = 70;

const isNonEmptyModules = (m) =>
  m &&
  typeof m === "object" &&
  Object.values(m).some((arr) => Array.isArray(arr) && arr.length > 0);

const isValidTarget = (t) => Number.isFinite(Number(t));

const buildEmptyModulesByLevel = (ruleset) => {
  const out = {};
  const levels = ruleset?.structure?.levels || [];
  for (const lvl of levels) out[lvl.key] = [];
  return out;
};

export function useRuleset({ universityId, degreeType }) {
  const [ruleset, setRuleset] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [modulesByLevel, setModulesByLevel] = useState({});
  const [targetGrade, setTargetGrade] = useState(DEFAULT_TARGET);
  const [openLevelKey, setOpenLevelKey] = useState(null);

  const uniIdStr = useMemo(
    () => (universityId == null ? "" : String(universityId)),
    [universityId]
  );
  const degStr = useMemo(
    () => (degreeType == null ? "" : String(degreeType).toLowerCase()),
    [degreeType]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!uniIdStr || !degStr) {
        setRuleset(null);
        setLoading(false);
        setLoadError("");
        return;
      }

      setLoading(true);
      setLoadError("");

      try {
        const rs = await getRuleset(uniIdStr, degStr);
        if (cancelled) return;

        setRuleset(rs);

        // 1) Ensure we have keys for current ruleset levels (but do not overwrite imported/user data)
        const empty = buildEmptyModulesByLevel(rs);
        setModulesByLevel((prev) => {
          if (isNonEmptyModules(prev)) {
            // Keep existing (e.g. import just set it)
            return prev;
          }
          // If prev is empty, initialize with correct keys for this ruleset
          return empty;
        });

        // 2) Default target grade only if user/import hasn't set one
        setTargetGrade((prev) => (isValidTarget(prev) ? prev : DEFAULT_TARGET));

        // 3) Default open level only if unset
        const firstKey = (rs?.structure?.levels || [])[0]?.key || null;
        setOpenLevelKey((prev) => prev ?? firstKey);
      } catch (e) {
        if (cancelled) return;
        setRuleset(null);
        setLoadError(e?.message || "Failed to load ruleset.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [uniIdStr, degStr]);

  return {
    ruleset,
    loading,
    loadError,

    modulesByLevel,
    setModulesByLevel,

    targetGrade,
    setTargetGrade,

    openLevelKey,
    setOpenLevelKey,
  };
}
